import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'
import { exportPolicyToDocx, exportPolicyToPdf } from '../../../shared/utils/policyExport'
import { exportStructuredPolicyToPdf } from '../utils/policyExport'
import { logAuditEvent } from '../../../shared/lib/auditLog'
import { useDemoPlan } from '../../../hooks/useDemoPlan'
import PolicyPreview from '../components/PolicyPreview'
import StructuredPolicyRenderer from '../../../shared/components/StructuredPolicyRenderer'

function relativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return date.toLocaleDateString()
}

function ClientPolicies({ clientIdProp }) {
  const { clientId: urlClientId } = useParams()
  const clientId = clientIdProp || urlClientId
  const { user } = useAuth()
  const { isDemoOrg, demoMessage } = useDemoPlan()

  const [policies, setPolicies] = useState([])
  const [acknowledgments, setAcknowledgments] = useState({})
  const [loading, setLoading] = useState(true)
  const [viewingPolicy, setViewingPolicy] = useState(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [acknowledging, setAcknowledging] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [demoNotice, setDemoNotice] = useState('')

  // Fetch published policies for this client
  useEffect(() => {
    if (!clientId) return
    const fetchPolicies = async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from('client_policies')
        .select('*')
        .eq('client_id', clientId)
        .eq('portal_visible', true)
        .order('domain')
        .order('title')

      if (error) {
        console.error('Error fetching policies:', error)
      } else {
        setPolicies(data || [])
      }
      setLoading(false)
    }
    fetchPolicies()
  }, [clientId])

  // Fetch acknowledgments for the current user
  useEffect(() => {
    if (!clientId || !user) return
    const fetchAcks = async () => {
      const { data } = await supabase
        .from('policy_acknowledgments')
        .select('client_policy_id, version_acknowledged, acknowledged_at')
        .eq('user_id', user.id)

      if (data) {
        const map = {}
        data.forEach(a => {
          map[a.client_policy_id] = a
        })
        setAcknowledgments(map)
      }
    }
    fetchAcks()
  }, [clientId, user])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 3000)
      return () => clearTimeout(t)
    }
  }, [successMessage])

  const handleAcknowledge = async (policy) => {
    setAcknowledging(true)
    try {
      const { error } = await supabase
        .from('policy_acknowledgments')
        .upsert({
          client_policy_id: policy.id,
          user_id: user.id,
          version_acknowledged: policy.current_version,
          acknowledged_at: new Date().toISOString()
        }, {
          onConflict: 'client_policy_id,user_id,version_acknowledged'
        })

      if (error) throw error

      setAcknowledgments(prev => ({
        ...prev,
        [policy.id]: {
          client_policy_id: policy.id,
          version_acknowledged: policy.current_version,
          acknowledged_at: new Date().toISOString()
        }
      }))
      setSuccessMessage('Policy acknowledged successfully')
      logAuditEvent({
        category: 'change',
        action: 'policy.acknowledged',
        description: `Acknowledged policy "${policy.title}" (v${policy.current_version})`,
        clientId,
        resourceType: 'policy',
        resourceId: policy.id
      })
    } catch (err) {
      console.error('Error acknowledging policy:', err)
    } finally {
      setAcknowledging(false)
    }
  }

  const isAcknowledged = (policy) => {
    const ack = acknowledgments[policy.id]
    return ack && ack.version_acknowledged >= policy.current_version
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      {/* Success toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {demoNotice && (
        <div className="fixed top-4 right-4 z-[60] bg-amber-600 text-white px-5 py-4 rounded-lg shadow-lg flex items-start gap-3 max-w-sm">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm font-semibold">Demo Mode</p>
            <p className="text-xs mt-0.5 text-amber-100">{demoNotice}</p>
          </div>
          <button onClick={() => setDemoNotice('')} className="ml-auto flex-shrink-0 text-amber-200 hover:text-white">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <h1 className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-2">Policies</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Review your organization's security policies and acknowledge receipt.
      </p>

      {policies.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400">No published policies available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {policies.map(policy => {
            const acked = isAcknowledged(policy)
            return (
              <div
                key={policy.id}
                onClick={() => { setViewingPolicy(policy); setShowExportMenu(false) }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer flex flex-col"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{policy.domain}</p>
                  {acked ? (
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Acknowledged
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      Pending Review
                    </span>
                  )}
                </div>

                <h3 className="text-sm font-semibold text-primary-600 dark:text-primary-400 truncate mb-3">
                  {policy.title}
                </h3>

                {(policy.framework_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {policy.framework_tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        {tag}
                      </span>
                    ))}
                    {policy.framework_tags.length > 3 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                        +{policy.framework_tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    v{policy.current_version} &middot; Published {relativeTime(policy.published_at || policy.updated_at)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Policy View Modal */}
      {viewingPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0 flex-1 mr-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">{viewingPolicy.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{viewingPolicy.domain} &middot; v{viewingPolicy.current_version}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Export */}
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                      <button
                        onClick={async () => {
                          setShowExportMenu(false)
                          if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
                          try {
                            await exportPolicyToDocx({
                              title: viewingPolicy.title,
                              domain: viewingPolicy.domain,
                              content: viewingPolicy.content,
                              frameworkTags: viewingPolicy.framework_tags || []
                            })
                          } catch (err) {
                            console.error('DOCX export failed:', err)
                          }
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export as .docx
                      </button>
                      <button
                        onClick={async () => {
                          setShowExportMenu(false)
                          if (isDemoOrg) { setDemoNotice(demoMessage('Policy downloads')); setTimeout(() => setDemoNotice(''), 8000); return }
                          setTimeout(async () => {
                            try {
                              if (viewingPolicy.structured_content) {
                                await exportStructuredPolicyToPdf(viewingPolicy, [], { name: viewingPolicy.client_name || '' }, null, '')
                              } else {
                                await exportPolicyToPdf({ title: viewingPolicy.title, elementId: 'policy-preview-export' })
                              }
                            } catch (err) {
                              console.error('PDF export failed:', err)
                            }
                          }, 200)
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Export as .pdf
                      </button>
                    </div>
                  )}
                </div>

                {/* Close */}
                <button onClick={() => { setViewingPolicy(null); setShowExportMenu(false) }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Policy content */}
            <div className="flex-1 overflow-auto p-6" id="policy-preview-export">
              {viewingPolicy.structured_content ? (
                <div className="bg-white shadow-lg max-w-[750px] mx-auto" style={{ minHeight: '600px' }}>
                  <StructuredPolicyRenderer structuredContent={viewingPolicy.structured_content} template={viewingPolicy} showRevisionHistory={false} />
                </div>
              ) : (
                <PolicyPreview content={viewingPolicy.content} />
              )}
            </div>

            {/* Footer — Acknowledge */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              {isAcknowledged(viewingPolicy) ? (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">
                    Acknowledged on {new Date(acknowledgments[viewingPolicy.id].acknowledged_at).toLocaleDateString()}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please review this policy and acknowledge receipt.
                </p>
              )}
              {!isAcknowledged(viewingPolicy) && (
                <button
                  onClick={() => handleAcknowledge(viewingPolicy)}
                  disabled={acknowledging}
                  className="btn-primary flex items-center gap-2"
                >
                  {acknowledging ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Acknowledging...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      I have reviewed and acknowledge this policy
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientPolicies
